/**
 * Social / leaderboard / activity-log request validators + DTO types (plan 23
 * §3.2 / §3.4). Consumed by the member-app feature logic, the /social Edge
 * handler, and the typed api-client caller. Leaderboards are opt-in
 * (consent_type='social_leaderboard'); activity logging is opt-in
 * (consent_type='health_data'). Cross-gym friend leaderboards respect consent
 * on BOTH sides (MS-01, MS-02).
 */
import { z } from 'zod';

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

export const FriendshipStatusSchema = z.enum(['pending', 'accepted', 'blocked']);
export type FriendshipStatus = z.infer<typeof FriendshipStatusSchema>;

export const ActivityVisibilitySchema = z.enum(['private', 'friends']);
export type ActivityVisibility = z.infer<typeof ActivityVisibilitySchema>;

export const LeaderboardPeriodSchema = z.enum(['weekly', 'monthly']);
export type LeaderboardPeriod = z.infer<typeof LeaderboardPeriodSchema>;

export const LeaderboardMetricSchema = z.enum(['visits', 'streak']);
export type LeaderboardMetric = z.infer<typeof LeaderboardMetricSchema>;

export const FriendRequestActionSchema = z.enum(['accept', 'decline', 'block']);
export type FriendRequestAction = z.infer<typeof FriendRequestActionSchema>;

// ---------------------------------------------------------------------------
// Friendship requests
// ---------------------------------------------------------------------------

/** POST /api/v1/social/friends */
export const CreateFriendRequestSchema = z.object({
  addressee_global_person_id: z.string().uuid(),
});
export type CreateFriendRequestInput = z.infer<typeof CreateFriendRequestSchema>;

/** POST /api/v1/social/friends/:id/respond */
export const RespondFriendRequestSchema = z.object({
  action: FriendRequestActionSchema,
});
export type RespondFriendRequestInput = z.infer<typeof RespondFriendRequestSchema>;

// ---------------------------------------------------------------------------
// Activity logging
// ---------------------------------------------------------------------------

export const ActivitySetEntrySchema = z.object({
  exercise: z.string().min(1).max(64),
  reps: z.number().int().min(1).max(1000).optional(),
  weight_kg: z.number().min(0).max(1000).optional(),
  duration_seconds: z.number().int().min(1).max(86_400).optional(),
});
export type ActivitySetEntry = z.infer<typeof ActivitySetEntrySchema>;

/** POST /api/v1/activity-logs */
export const CreateActivityLogSchema = z.object({
  activity_type: z.string().min(1).max(64),
  duration_minutes: z.number().int().min(1).max(600),
  sets: z.array(ActivitySetEntrySchema).max(60).optional(),
  photo_path: z.string().max(512).optional(),
  logged_at: z.string().datetime({ offset: true }).optional(),
  visibility: ActivityVisibilitySchema.default('private'),
});
export type CreateActivityLogInput = z.infer<typeof CreateActivityLogSchema>;

// ---------------------------------------------------------------------------
// Leaderboard queries
// ---------------------------------------------------------------------------

/** GET /api/v1/social/leaderboard/{gym|friends} */
export const LeaderboardQuerySchema = z.object({
  period: LeaderboardPeriodSchema.default('weekly'),
  metric: LeaderboardMetricSchema.default('visits'),
  limit: z.number().int().min(1).max(100).default(50),
});
export type LeaderboardQueryInput = z.infer<typeof LeaderboardQuerySchema>;

// ---------------------------------------------------------------------------
// DTO shapes (transport)
// ---------------------------------------------------------------------------

export interface FriendshipRow {
  id: string;
  tenant_id: string;
  requester_global_person_id: string;
  addressee_global_person_id: string;
  status: FriendshipStatus;
  created_at: string;
}

export interface FriendProfile {
  global_person_id: string;
  display_name: string;
  gym_name: string;
  friendship_status: FriendshipStatus;
  /** 'incoming' = they requested us; 'outgoing' = we requested them; null when accepted. */
  direction: 'incoming' | 'outgoing' | null;
}

export interface LeaderboardEntryDTO {
  rank: number;
  member_id: string;
  global_person_id: string | null;
  display_name: string;
  gym_name: string | null;
  value: number;
  metric: LeaderboardMetric;
  period: LeaderboardPeriod;
  is_self: boolean;
  is_friend: boolean;
  computed_at: string | null;
}

export interface ActivityLogRow {
  id: string;
  tenant_id: string;
  member_id: string;
  activity_type: string;
  duration_minutes: number;
  sets: ActivitySetEntry[] | null;
  photo_path: string | null;
  logged_at: string;
  visibility: ActivityVisibility;
  created_at: string;
}
